import type { TKeysToCamelCase, TKeysToSnakeCase } from '../types/app.types';

export function debounce<T extends unknown[], U>(
  callback: (...args: T) => PromiseLike<U> | U,
  wait: number
): (...args: T) => Promise<U> {
  let timer: ReturnType<typeof setTimeout> | undefined;

  return (...args: T): Promise<U> => {
    clearTimeout(timer);
    return new Promise((resolve) => {
      timer = setTimeout(() => resolve(callback(...args)), wait);
    });
  };
}

export function throttle<T extends unknown[], U>(
  func: (...args: T) => U,
  limit: number
): (...args: T) => void {
  let lastFunc: ReturnType<typeof setTimeout>;
  let lastRan: number;

  return (...args: T) => {
    if (!lastRan) {
      func(...args); // Call the function immediately
      lastRan = Date.now();
    } else {
      clearTimeout(lastFunc);
      lastFunc = setTimeout(
        () => {
          if (Date.now() - lastRan >= limit) {
            func(...args); // Call the function after the delay
            lastRan = Date.now();
          }
        },
        limit - (Date.now() - lastRan)
      );
    }
  };
}

/**
 * Конвертирует строку в camelCase.
 * Поддерживает форматы snake_case, kebab-case, PascalCase и camelCase.
 *
 * @param {string} str - Строка для преобразования.
 * @returns {string} - Строка, приведённая к camelCase.
 *
 * @example
 * stringToCamelCase('example_string');
 * // Returns: 'exampleString'
 *
 * stringToCamelCase('ExampleString');
 * // Returns: 'exampleString'
 *
 * stringToCamelCase('example-string');
 * // Returns: 'exampleString'
 *
 * stringToCamelCase('exampleString');
 * // Returns: 'exampleString'
 */
export const stringToCamelCase = (str: string) => {
  return str
    .replace(/(\w)?[_-](\w)/g, (_, c, d) => {
      if (!c) return d.toUpperCase();
      return c.toLowerCase() + d.toUpperCase();
    })
    .replace(/^\w/, (c) => c.toLowerCase());
};

/**
 * Рекурсивно конвертирует ключи объектов и массивов в camelCase.
 * Значения примитивных типов (string, number, boolean и т.д.) не изменяются.
 *
 * @template T - Тип входного значения
 * @param {T} input - Объект, массив или значение для преобразования.
 * @returns {TKeysToCamelCase<T>} Новый объект/массив с ключами в camelCase.
 *
 * @example
 * const obj = {
 *   snake_case_key: 1,
 *   PascalKey: 2,
 *   nested_object: { another_snake_case: 3 },
 *   snake_array: [{ test_value: 4 }],
 * };
 *
 * keysToCamelCase(obj);
 * // Returns:
 * // {
 * //   snakeCaseKey: 1,
 * //   pascalKey: 2,
 * //   nestedObject: { anotherSnakeCase: 3 },
 * //   snakeArray: [{ testValue: 4 }]
 * // }
 */
export const keysToCamelCase = <T>(input: T): TKeysToCamelCase<T> => {
  if (Array.isArray(input)) {
    return input.map((item) => keysToCamelCase(item)) as TKeysToCamelCase<T>;
  }

  if (input && typeof input === 'object' && !Array.isArray(input)) {
    const newObj: Record<string, unknown> = {};
    Object.entries(input).forEach(([key, value]) => {
      newObj[stringToCamelCase(key)] =
        value && typeof value === 'object' ? keysToCamelCase(value) : value;
    });
    return newObj as TKeysToCamelCase<T>;
  }

  return input as TKeysToCamelCase<T>;
};

/**
 * Конвертирует строку в snake_case.
 * Поддерживает форматы camelCase, PascalCase, kebab-case и snake_case.
 *
 * @param {string} str - Строка для преобразования.
 * @returns {string} - Строка, приведённая к snake_case.
 *
 * @example
 * stringToSnakeCase('exampleString');
 * // Returns: 'example_string'
 *
 * stringToSnakeCase('ExampleString');
 * // Returns: 'example_string'
 *
 * stringToSnakeCase('example-string');
 * // Returns: 'example_string'
 *
 * stringToSnakeCase('example_string');
 * // Returns: 'example_string'
 */
export const stringToSnakeCase = (str: string): string => {
  return (
    str
      // Нормализуем разделители: "-" и пробелы → "_"
      .replace(/[-\s]+/g, '_')
      // HTTPServer → HTTP_Server (граница между серией верхних и "Верхняя+нижняя/цифра")
      .replace(/([A-Z]+)([A-Z][a-z0-9])/g, '$1_$2')
      // fooBar → foo_Bar (граница "нижняя/цифра → верхняя")
      .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
      // Схлопываем двойные "__" и убираем крайние
      .replace(/__+/g, '_')
      .replace(/^_+|_+$/g, '')
      // Всё в нижний регистр
      .toLowerCase()
  );
};

/**
 * Рекурсивно конвертирует ключи объектов и массивов в snake_case.
 * Значения примитивных типов (string, number, boolean и т.д.) не изменяются.
 *
 * @template T - Тип входного значения
 * @param {T} input - Объект, массив или значение для преобразования.
 * @returns {TKeysToSnakeCase<T>} Новый объект/массив с ключами в snake_case.
 *
 * @example
 * const obj = {
 *   camelCaseKey: 1,
 *   PascalKey: 2,
 *   nestedObject: { anotherCamelCase: 3 },
 *   camelArray: [{ testValue: 4 }],
 * };
 *
 * keysToSnakeCase(obj);
 * // Returns:
 * // {
 * //   camel_case_key: 1,
 * //   pascal_key: 2,
 * //   nested_object: { another_camel_case: 3 },
 * //   camel_array: [{ test_value: 4 }]
 * // }
 */
export const keysToSnakeCase = <T>(input: T): TKeysToSnakeCase<T> => {
  if (Array.isArray(input)) {
    return input.map((item) => keysToSnakeCase(item)) as TKeysToSnakeCase<T>;
  }

  if (input && typeof input === 'object' && !Array.isArray(input)) {
    const newObj: Record<string, unknown> = {};
    Object.entries(input).forEach(([key, value]) => {
      newObj[stringToSnakeCase(key)] =
        value && typeof value === 'object' ? keysToSnakeCase(value) : value;
    });
    return newObj as TKeysToSnakeCase<T>;
  }

  return input as TKeysToSnakeCase<T>;
};
